import { useRequest } from 'ahooks';
import { pull } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
// -------------- api -------------------- //
import { fetchDeleteWish, fetchGetWishList } from '@/app/csr-src/apis/wishList/wishList.api';
import type { ProductInfo } from '@/app/csr-src/apis/wishList/wishList.interface';
// -------------- 组件 -------------------- //
import JDIProductCard from '@/components/productCard/index';
import UKPageError from '@/components/UKPageError';
import UKPageLoading from '@/components/UKPageLoading';
import UKPageNothing, { DefaultImageType } from '@/components/UKPageNothing';
import UKToast from '@/components/UKToast/index';
import Pagination from '../../order/components/pagination';
import TooltipCom from '../components/TooltipCom';
// -------------- styles -------------------- //
import './index.scss';
// -------------- utils -------------------- //
import { getPromotionObj } from '@/components/productCard/common/utils';
import { ProductState, ProductType } from '@/components/productCard/index.interface';
import { EXPCONFIG_FAST } from '@/constants/point';
import { EXPOSURE_RECORD, sendClickTrack, sendPv } from '@/tracks/25148';
import { PROFILE_EVENT_NAMES, wishListBusinessLogger } from '@/utils/logger/instance/profileLogger';
import { Button, Checkbox } from '@mui/material';

interface ProfileListContext {
  updateNumber: () => void;
  initCartNumHandler: () => void;
}

const WishList = () => {
  // 接收父级传入的参数
  const { updateNumber } = useOutletContext<ProfileListContext>();
  const { t } = useTranslation();
  const [isNothing, setIsNothing] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState({
    currentPage: 1,
    pageSize: 12,
  });
  const [total, setTotal] = useState(0); // 总条数

  const [currencySymbol, setCurrencySymbol] = useState('');
  const [selectedSKUList, setSelectedSKUList] = useState([]);
  const isInitialMount = useRef(true);

  // 点击批量删除按钮
  const clickAllDelete = async () => {
    try {
      // 批量删除埋点
      sendClickTrack.MyJD_Wishlist_Delete({
        skuid: (selectedSKUList && selectedSKUList.join('#')) || '',
      });
      // 业务监控
      wishListBusinessLogger({
        eventName: PROFILE_EVENT_NAMES.WISHLIST_DELETE,
        message: {
          skuIds: (selectedSKUList && selectedSKUList.join('#')) || '',
        },
      });
    } catch (error) {}

    // 校验选中列表是否为空
    if (!selectedSKUList?.length) {
      UKToast.show({
        content: t('common.message.PleaseSelectAtLeastOneProduct'), // 弹窗内容
        icon: 'warning', // 可选：success, fail, loading, warn, info
        duration: 1000,
      });
      return;
    }
    const params = {
      bizIds: selectedSKUList,
      bizType: 1,
    };
    deleteWish(params);
  };
  // 点击删除按钮
  const clickDelete = async (item: ProductInfo): Promise<void> => {
    const params = {
      bizIds: [item.skuId],
      bizType: 1,
    };
    await deleteWish(params);
  };
  const deleteWish = async (params: any) => {
    const res = await fetchDeleteWish(params);
    if (res.code === '200') {
      UKToast.show({
        content: t('common.message.deleteSuccessful'), // 弹窗内容
        icon: 'success', // 可选：success, fail, loading, warn, info
        duration: 3000,
      });
      setPage({
        ...page,
        pageSize: 12,
        currentPage: 1,
      });
      setTotal(0);
      setIsOpenEdit(false);
      updateNumber();
      getWishList();
    } else {
      UKToast.show({
        content: res.msg, // 弹窗内容
        icon: 'fail', // 可选：success, fail, loading, warn, info
        duration: 3000,
      });
      try {
        wishListBusinessLogger({
          eventName: PROFILE_EVENT_NAMES.WISHLIST_DELETE_FAIL,
          message: {
            msg: res.msg,
            skuIds: (selectedSKUList && selectedSKUList.join('#')) || '',
          },
        });
      } catch (error) {}
    }
  };

  // 分页change事件
  const pagesChangeHandler = (pageNum: number) => {
    window.scrollTo(0, 0); // 滚动到顶部
    setPage({
      ...page,
      pageSize: 12,
      currentPage: pageNum,
    });
    setTotal(0);
  };

  const getWishList = async () => {
    setIsLoading(true);
    setIsError(false);
    const { pageSize, currentPage } = page;
    const params = {
      pageSize,
      currentPage,
      bizType: 1,
    };
    const res = await fetchGetWishList(params);
    if (res.code === '200' && res.data) {
      const { data, total, localInfo } = res.data;
      if (data && data.length) {
        // 格式化数据结构
        const new_data: any = []; // 处理成商卡需要的字段
        data.forEach((v: any, i: number) => {
          const new_item = getProductData(v, i, localInfo?.currencySymbol);
          // @ts-ignore
          if (selectedSKUList.includes(new_item.skuId)) {
            // @ts-ignore
            new_item.selected = true;
          }
          new_data.push(new_item);
        });
        setData(new_data || []);
        setIsNothing(false);
        setIsLoading(false);
        setTotal(total);
      } else {
        setIsNothing(true);
      }
      setCurrencySymbol(localInfo?.currencySymbol);
      setIsError(false);
    } else {
      setIsError(true);
    }
    isInitialMount.current = false;
    setIsLoading(false);
  };
  // 点击单个商品删除按钮防抖
  const { run: tooltipClickDelete } = useRequest(clickDelete, {
    debounceWait: 500,
    debounceLeading: true,
    manual: true,
  });
  // 点击删除选中商品按钮防抖
  const { run: handleClickDelete } = useRequest(clickAllDelete, {
    debounceWait: 500,
    debounceLeading: true,
    manual: true,
  });
  //统一处理埋点参数
  const getJsonParam = (item: any, index: number) => {
    return {
      index,
      skuid: item?.skuId || '-100',
      secprice: item?.originPrice || '-100',
      secpricetype: '83' as const,
      firprice: item?.realPrice || '-100',
      firpricetype: '11' as const,
      freqTag: '-100',
      sortName: '-100',
      filter_name: '-100',
      ...getPromotionObj(item?.promotions || ''), // 处理营销标签埋点（包含：couponInfo、single_promotion、amount_promotion、time_label）
    };
  };
  const getTrackingPointParams = (v: any, index: number) => {
    const jsonParam = getJsonParam(v, index);
    return {
      productExpConfig: {
        event_id: EXPOSURE_RECORD.MyJD_Wishlist_Product_Expo,
        config: {
          useClick: false,
          stay_duration: 0.01,
          repeated: false,
          area_rate: 0.01,
        },
        json_param: jsonParam,
      },
      cartExpConfig: {
        event_id: EXPOSURE_RECORD.MyJD_Wishlist_Product_Addtocart_Expo,
        config: {
          useClick: false,
          stay_duration: 0.3,
          repeated: false,
          area_rate: 0.5,
        },
        json_param: jsonParam,
      },
      productClickPoint: () => sendClickTrack.MyJD_Wishlist_Product(jsonParam),
      cartClickPoint: () => sendClickTrack.MyJD_Wishlist_Product_Addtocart(jsonParam),
    };
  };
  const getProductData = (item: any, index: number, currency: string) => {
    /*
      处理营销标签 promoTagInfo
    */
    const promotions: any = [];
    if (item?.promoTagInfo) {
      const promoTagInfo = item.promoTagInfo;
      Object.keys(promoTagInfo).forEach((key) => {
        promotions.push({ showStyle: promoTagInfo[key] });
      });
    }
    let subscribeType, subscribeStatus;
    if (item?.subscribeList && item?.subscribeList.length) {
      const targetItem = item.subscribeList.find((item: any) => item.subscribeType === 0);
      subscribeType = targetItem.subscribeType;
      subscribeStatus = targetItem.subscribeStatus;
    }

    const productData = {
      from: 'wishList',
      type: ProductType.TYPE_3,
      productState: item.tagList?.includes(5)
        ? ProductState.REMOVED
        : item.tagList?.includes(4)
          ? ProductState.OUT_OF_STICK
          : ProductState.IN_STOCK,
      unitPriceText: item.unitShow,
      contentStyle: { marginTop: '16px' },
      skuName: item.skuName,
      skuImgKeyParams: {
        imgUrlKey: item.imgUrl,
        width: 240,
        height: 240,
      },
      skuId: item.skuId,
      skuImgStyle: { height: '240px' },
      realPrice: item.realPrice,
      originalPrice: item.originPrice,
      currency,
      saleInfo: item.saleInfo, // 规格 Todo前
      score: item.score,
      shouldShowCart: !isOpenEdit,
      onClickProduct: !isOpenEdit
        ? null
        : () => {
            return;
          },
      isShowWish: false,
      promotions: promotions?.length ? promotions : [], // 营销标签数组
      trackingPointParams: getTrackingPointParams(item, index),
      subscribeType,
      subscribeStatus,
      NotifyCallback: (states: boolean) => states && getWishList(),
      shouldShowModal: true,
      presellInfo: item.preSellVO,
      isMember: item.memberSupported,
      memberPrice: item.plusPrice,
      memberPriceDesc: item.plusPriceDesc,
      memberPriceIcon: item.plusLogoUrl,
    };
    return productData;
  };

  const onRefresh = (val: any) => {
    const { selected, skuId } = val;
    const selectedSKUs: any = [...selectedSKUList];
    selected ? selectedSKUs.push(skuId) : pull(selectedSKUs, skuId);
    data.forEach((v: any, i: any) => {
      if (v.skuId === skuId) {
        v.selected = selected;
      }
    });

    setSelectedSKUList(selectedSKUs);
    setData(data);
  };

  const upDateEditStatus = () => {
    const currentIsOpenEdit = !isOpenEdit;
    setIsOpenEdit(currentIsOpenEdit);
    sendClickTrack.MyJD_Wishlist_Edit();
  };

  const upDateDoneStatus = () => {
    const currentIsOpenEdit = !isOpenEdit;
    setIsOpenEdit(currentIsOpenEdit);
    sendClickTrack.MyJD_Wishlist_Done();
  };

  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    getWishList();
  }, [page]);

  useEffect(() => {
    setSelectedSKUList([]);
  }, [isOpenEdit]);

  useEffect(() => {
    getWishList();
    sendPv.MyJD_Wishlist_Main();
  }, []);

  return (
    <div id='wishList_page_container' className='withList'>
      <Helmet>
        <title>{`${t('seo.title.wishlist')} | Joybuy`}</title>
        <meta name='description' content={t('seo.description.allDesc')} />
      </Helmet>
      <div className='title_container'>
        <div className='title' onClick={updateNumber}>
          {t('common.pageName.wishlist')}
        </div>
        {!isNothing && !isError && !isLoading && (
          <div className='EditBtns'>
            {isOpenEdit ? (
              <div className='displayFlex'>
                <Button className='EditBtn' onClick={() => upDateDoneStatus()}>
                  <span
                    exptag={'exp|' + EXPOSURE_RECORD.MyJD_Wishlist_Done_Expo}
                    data-exptag-config={EXPCONFIG_FAST}
                  >
                    {t('common.btnText.done')}
                  </span>
                </Button>
                <Button className='deleteBtn' onClick={() => clickAllDelete()}>
                  <span
                    exptag={'exp|' + EXPOSURE_RECORD.MyJD_Wishlist_Delete_Expo}
                    data-exptag-config={EXPCONFIG_FAST}
                  >
                    {t('common.btnText.delete')}
                  </span>
                </Button>
              </div>
            ) : (
              <Button className='EditBtn' onClick={() => upDateEditStatus()}>
                <span
                  exptag={'exp|' + EXPOSURE_RECORD.MyJD_Wishlist_Edit_Expo}
                  data-exptag-config={EXPCONFIG_FAST}
                >
                  {t('common.btnText.edit')}
                </span>
              </Button>
            )}
          </div>
        )}
      </div>
      {isNothing && (
        <div className='errorPage'>
          <UKPageNothing
            defaultImageType={DefaultImageType.WISH_NOTHING}
            style={{ marginTop: '60px' }}
          />
        </div>
      )}
      {isError && (
        <div className='errorPage'>
          <UKPageError style={{ marginTop: '60px' }} clickTryAgain={() => getWishList()} />
        </div>
      )}
      {isLoading && (
        <div className='errorPage'>
          <UKPageLoading />
        </div>
      )}
      {!isNothing && !isError && !isLoading && (
        <>
          <div className='itemList'>
            {data && data.length > 0
              ? data.map((v: any, i: any) => {
                  return (
                    <div key={v.skuId} className='ProductCardItem'>
                      <div className='operationBox'>
                        {/* 复选框 */}
                        {isOpenEdit ? (
                          <Checkbox
                            sx={{
                              padding: 0,
                              margin: '0 8px 0 0',
                              color: '#E0E1E5',
                              '&.Mui-checked': {
                                color: '#1A1A1A',
                              },
                            }}
                            key={v.skuId}
                            // checked={!!v.selected}
                            onClick={(event) => {
                              const target = event.target as HTMLInputElement;
                              onRefresh({
                                skuId: v?.skuId,
                                selected: target.checked,
                              });
                            }}
                          />
                        ) : (
                          <div></div>
                        )}
                        {!isOpenEdit ? (
                          <TooltipCom
                            ProductItem={v}
                            deleteFn={() => {
                              clickDelete(v);
                            }}
                          />
                        ) : null}
                      </div>
                      <JDIProductCard
                        productData={{
                          ...v,
                          shouldShowCart: !isOpenEdit,
                          onClickProduct: !isOpenEdit,
                        }}
                        key={JSON.stringify(v)}
                      />
                    </div>
                  );
                })
              : null}
          </div>
          {total > 12 ? (
            <div className='pagination_wrap'>
              <Pagination
                currentPage={page.currentPage}
                totalPages={Math.ceil(total / page.pageSize)}
                handlePageChange={pagesChangeHandler}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};
export default WishList;
